import { z } from "zod";
import { ValidatorInterface } from "../validator-interface";
import { isEmpty } from "../utils/validator-utils";

export class MinValueValidator extends ValidatorInterface {
    getZodSchema(baseSchema, options = {}) {
        const minValue = parseFloat(options.value);
        const labelText = options.labelText || "This field";
        const message = options.message || `${labelText} must be at least ${minValue}`;

        if (isNaN(minValue)) {
            // eslint-disable-next-line no-console
            console.warn("Invalid minValue provided for MinValueValidator:", options.value);
            return baseSchema; // Return base schema if minValue is invalid
        }

        // Apply to number schemas
        if (baseSchema instanceof z.ZodNumber) {
            return baseSchema.min(minValue, { message });
        }
        // Apply to string schemas that should be converted to numbers
        else if (baseSchema instanceof z.ZodString) {
            // First transform string to number, then apply min constraint
            return baseSchema
                .transform(val => {
                    if (isEmpty()) {
                        return val; // Let optional() handle empty values
                    }
                    const parsed = parseFloat(val);
                    return isNaN(parsed) ? val : parsed; // Return original if not a number
                })
                .refine(
                    val => {
                        if (isEmpty(val)) {
                            return true; // Let optional() handle this
                        }
                        const num = typeof val === "number" ? val : parseFloat(val);
                        return !isNaN(num) && num >= minValue;
                    },
                    { message }
                );
        }
        // Apply to transformed schemas (ZodEffects) - common for number fields
        else if (baseSchema instanceof z.ZodEffects) {
            return baseSchema.refine(
                val => {
                    if (isEmpty(val)) {
                        return true; // Let optional() handle this
                    }
                    const num = typeof val === "number" ? val : parseFloat(val);
                    return !isNaN(num) && num >= minValue;
                },
                { message }
            );
        }

        // eslint-disable-next-line no-console
        console.warn("MinValueValidator applied to unsupported schema type. Requires ZodNumber, ZodString, or ZodEffects.");
        return baseSchema; // Return base schema if type is not supported
    }

    getErrorMessage(field, options = {}) {
        const minValue = parseFloat(options.value);
        const labelText = options.labelText || "This field";
        if (isNaN(minValue)) {
            return "Invalid minimum value requirement specified.";
        }
        return options.message || `${labelText} must be at least ${minValue}`;
    }
}
